import type { Move } from '../../../schemas/move';
import { barbaroArchetypeMoves } from './archetypes';
import {
  barbaroInitialMoves,
  barbaroStartingAutoMoves,
  barbaroDefenseStyleMoves,
  barbaroInstinctMoves,
} from './initial';
import { barbaroAdvanced2to5Moves } from './advanced-2-5';
import { barbaroAdvanced6to10Moves } from './advanced-6-10';

export {
  barbaroArchetypeMoves,
  barbaroInitialMoves,
  barbaroStartingAutoMoves,
  barbaroDefenseStyleMoves,
  barbaroInstinctMoves,
  barbaroAdvanced2to5Moves,
  barbaroAdvanced6to10Moves,
};

/**
 * Catalogo completo delle mosse del Barbaro (manuale V1, sezione *Il Barbaro*,
 * rr. 1931-2233).
 *
 * Aggregato di:
 *  - 3 archetipi origine-tematici (`class_special`): Straniero / Selvaggio /
 *    Nomade. Unica eccezione delle 12 classi DWR a NON avere il legame
 *    razza→archetipo (`associatedRace: undefined`).
 *  - 3 mosse iniziali automatiche (`starting`): Giramondo / Appetiti Erculei
 *    (con `choices` 6, `choiceCount: 2`) / Dominare la Morte.
 *  - 2 Stili di Difesa (`starting`, gruppo iniziale `pickCount: 1`): Coperto
 *    d'Acciaio / Mai Impacciato Sempre Illeso.
 *  - 3 Istinti (`starting`, gruppo iniziale `pickCount: 1`,
 *    **`expandableInAdvanced: true`**): Avventura / Battaglia / Misticismo
 *    (Animale Totemico, con `choices` 4 specie, `choiceCount: 1`).
 *  - 16 mosse avanzate L2-5 (`advanced_2_5`)
 *  - 14 mosse avanzate L6-10 (`advanced_6_10`)
 *
 * Totale: **41 mosse**.
 *
 * **Pattern stress-testati nella PR Barbaro (12/12 — Fase C COMPLETATA)**:
 *
 *  - **`suggestedTitles` popolato (12 titoli)** — PRIMO USO del campo fra le
 *    12 classi DWR. Le altre 11 classi hanno tutte `suggestedTitles: []`. Il
 *    Barbaro è l'unica classe con sezione «Titoli» nel manuale (r. 1944).
 *
 *  - **`StartingMoveChoiceGroup.expandableInAdvanced: true` sugli Istinti**
 *    — PRIMO USO STRUTTURALE del flag fra le 12 classi DWR. Lo schema lo
 *    definisce con `default(false)` proprio per gli Istinti del Barbaro
 *    (commento esplicito in `src/schemas/classDefinition.ts`). Il "consumatore
 *    narrativo" è la mossa avanzata *Istinti Primordiali* (L2-5,
 *    description-only) che concede al level-up un Istinto rimanente.
 *
 *  - **3 archetipi origine-tematici** (`associatedRace: undefined`): Straniero
 *    / Selvaggio / Nomade. Schema `Archetype.associatedRace` è opzionale
 *    proprio per questa eccezione.
 *
 *  - ***Appetiti Erculei*** (mossa-config iniziale auto): `MoveSchema.choices`
 *    su 6 appetiti, `choiceCount: 2`. Pattern coerente con *Faro di Devozione*
 *    del Paladino, *Nato dalla Terra* del Druido, *Avvelenatore* del Ladro.
 *
 *  - ***Misticismo: Animale Totemico*** (Istinto, mossa-config):
 *    `MoveSchema.choices` su 4 specie (aquila/corvo/lupo/orso),
 *    `choiceCount: 1`. La specie scelta è il trigger semantico per *Essere il
 *    Totem* (L6-10, FI-30 stub description-only).
 *
 *  - ***Potenza Indomabile*** sostituisce ***Assalto Irruento*** —
 *    **7° applicazione strutturale di `MoveSchema.replaces`** nelle 12 classi
 *    DWR (precedenti: 2 Mago, 2 Monaco, 1 Ranger, 3 Paladino).
 *
 *  - **FI-8 — *Banda di Guerra***: gregario L2 con Costo «Soddisfare uno
 *    dei tuoi appetiti», 5 PF. Strutturato via `FollowerTemplate`
 *    (`barbaro-banda-di-guerra.ts`).
 *  - **FI-64 — *Il Khan dei Khan*** (Sprint C 2026-05-21): L6-10 evolve la
 *    Banda di Guerra a gregario L3 (8 PF, mod +3). Upgrade strutturale
 *    applicato a runtime via `FollowerTemplate.upgrades` sulla Banda di
 *    Guerra (campi `levelOverride: 3`, `hpRuleOverride: fixed 8`,
 *    `modifierBonusOverride: amount 3`). `applyUpgrades` di
 *    `followerUpgrades.ts` legge `Character.moves` e sostituisce i campi
 *    base del template prima di costruire il Follower.
 *
 *  - **FI-30 nuovo (P3)**: ***Essere il Totem*** (L6-10) pattern UNICO «mossa
 *    che concede mossa specifica di altra classe non-multiclasse». Differente
 *    da FI-17 (multiclasse aperto): concessione predeterminata da scelta
 *    strutturata interna alla classe-source (*Animale Totemico*).
 *    Description-only.
 *
 *  - **FI-31 nuovo (P3)**: pattern trasversale «mossa avanzata che concede
 *    sotto-scelta da un set della classe». ***Istinti Primordiali*** +
 *    ***Ho Ancora Fame*** del Barbaro, coerenti con *Allargare le Basi*
 *    (Chierico), *Mille Risorse* (Ladro), *Di Tutto un Po'* (Bardo),
 *    *Formazione Eterogenea* (Mago), *Bambole e Pendoli* (Sciamano).
 *    Description-only. Modellazione attesa alla feature di level-up.
 *
 *  - **Mosse multiclasse** (FI-17): *Fame per La Distruzione* (L2-5) e
 *    *Ammazzali Tutti* (L6-10) scelgono da Guerriero/Bardo/Ladro/Ranger/
 *    Sciamano. Description-only.
 *
 *  - **Catene di prerequisiti strutturate**:
 *    - 3 mosse req *Appetiti Erculei* (auto): *Banda di Guerra*, *Ho Ancora
 *      Fame*, *Fame Insaziabile*, + *Un Segno di Forza* (L6-10).
 *    - 2 mosse req *Ira* (L2-5): *Berserker*, *Ira Persistente* (entrambe
 *      L6-10).
 *    - *Il Khan dei Khan* (L6-10) req *Banda di Guerra* (L2-5) — catena
 *      gregario L2 → L3.
 *    - *Essere il Totem* (L6-10) req *Misticismo: Animale Totemico* (Istinto
 *      iniziale).
 *
 *  - **Forward-refs cross-class TARGET**: nessuna mossa Barbaro è target di
 *    `prerequisites` strutturati cross-class dalle 11 classi già trascritte
 *    (verificato con grep). Le citazioni textuali del Barbaro nelle altre
 *    classi (es. Ranger *Abilità Secondarie* / *Addestramento Avanzato* nei
 *    pool multiclasse di 5 classi, Sciamano *Amico degli Spiriti* / *Figlio degli Spiriti*
 *    aperti a Barbaro o Druido) sono **description-only** (FI-17). Nessun
 *    `knownExternalIds` con `barbaro.*` da rimuovere nei test delle altre
 *    classi (nessun consolidamento D7 da fare nella PR Barbaro).
 *
 *  - **Cross-class refs textuali DAL Barbaro** (no strutturazione necessaria
 *    in questa PR): *Essere il Totem* cita 4 ID di altre classi
 *    (`ladro.io-sparo-per-primo`, `guerriero.vedere-rosso`,
 *    `paladino.attacco-a-seguire`, `paladino.difensore-devoto`), tutti
 *    esistenti localmente nei rispettivi cataloghi delle 11 classi precedenti.
 *    *Fame per La Distruzione* / *Ammazzali Tutti* description-only senza ID
 *    specifici (multiclasse aperto).
 */
export const barbaroMoves: readonly Move[] = [
  ...barbaroArchetypeMoves,
  ...barbaroInitialMoves,
  ...barbaroAdvanced2to5Moves,
  ...barbaroAdvanced6to10Moves,
];
